import React, { useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { MAX_IMAGEM_AVISO, sanitizarAviso } from '../Shared/Utils/avisos';

export default function EditorAviso({ initialHtml, onChange, disabled }) {
  const host = useRef(null);
  const editor = useRef(null);
  const input = useRef(null);
  const selection = useRef(null);
  const change = useRef(onChange);
  const initial = useRef(initialHtml);
  const [error, setError] = useState('');
  change.current = onChange;

  const inserirImagens = async (range, files) => {
    const quill = editor.current;
    if (!quill || !quill.isEnabled()) return;
    let index = range?.index ?? quill.getLength() - 1;
    for (const file of files) {
      if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type) || file.size > MAX_IMAGEM_AVISO) {
        setError('Use imagens PNG, JPG, GIF ou WebP de até 5 MB.');
        continue;
      }
      try {
        const url = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        if (editor.current !== quill || !quill.isEnabled()) return;
        quill.insertEmbed(index, 'image', url, 'user');
        index += 1;
        quill.setSelection(index, 0, 'silent');
        setError('');
      } catch {
        setError('Não foi possível ler a imagem. Selecione o arquivo novamente.');
      }
    }
  };
  const inserirRef = useRef(inserirImagens);
  inserirRef.current = inserirImagens;

  useEffect(() => {
    const container = host.current;
    const mount = document.createElement('div');
    container.appendChild(mount);
    const quill = new Quill(mount, {
      theme: 'snow',
      placeholder: 'Escreva a mensagem que será exibida aos clientes…',
      formats: ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'link', 'image', 'color', 'background', 'align', 'blockquote'],
      modules: {
        toolbar: {
          container: [[{ header: [1, 2, 3, false] }], ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }], [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }], ['blockquote', 'link', 'image'], ['clean']],
          handlers: { image() { selection.current = quill.getSelection(true); input.current.click(); } },
        },
        uploader: {
          mimetypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
          handler(range, files) { inserirRef.current(range, files); },
        },
      },
    });
    editor.current = quill;
    quill.root.id = 'conteudo-aviso';
    quill.root.setAttribute('role', 'textbox');
    quill.root.setAttribute('aria-label', 'Mensagem do aviso');
    quill.root.setAttribute('aria-multiline', 'true');
    quill.clipboard.dangerouslyPasteHTML(sanitizarAviso(initial.current), 'silent');
    const update = () => change.current(quill.getSemanticHTML());
    quill.on('text-change', update);
    const labels = { header: 'Estilo do texto', bold: 'Negrito', italic: 'Itálico', underline: 'Sublinhado',
      strike: 'Riscado', color: 'Cor do texto', background: 'Cor de fundo', list: 'Lista', align: 'Alinhamento',
      blockquote: 'Citação', link: 'Inserir link', image: 'Inserir imagem', clean: 'Limpar formatação' };
    container.querySelectorAll('.ql-toolbar button, .ql-toolbar select, .ql-picker-label').forEach(control => {
      const name = Object.keys(labels).find(key => control.classList.contains(`ql-${key}`) || control.parentElement.classList.contains(`ql-${key}`));
      if (name) {
        control.setAttribute('aria-label', labels[name]);
        control.setAttribute('title', labels[name]);
      }
      if (control.tagName === 'BUTTON') control.type = 'button';
    });
    return () => {
      quill.off('text-change', update);
      quill.disable();
      editor.current = null;
      container.replaceChildren();
    };
  }, []);

  useEffect(() => { editor.current?.enable(!disabled); }, [disabled]);

  return <>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    <div ref={host} className={`aviso-editor${disabled ? ' aviso-editor-disabled' : ''}`} />
    <input ref={input} type="file" hidden accept="image/png,image/jpeg,image/gif,image/webp" onChange={event => {
      inserirImagens(selection.current, [...event.target.files]);
      event.target.value = '';
    }} />
    <p className="text-muted small mt-2">Use o botão de imagem para inserir arquivos entre os parágrafos. Até 5 MB por imagem.</p>
  </>;
}
